import { useNavigate } from '@tanstack/react-router'
import { tokens } from '@/locales/translations/tokens'
import { motion } from 'framer-motion'
import {
  Mail,
  Linkedin,
  Instagram,
  Facebook,
  Youtube,
  ExternalLink,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { legalLinks } from '@/features/legal/legal-content'
import ContactUsSection from '../contactUs'

const Footer = () => {
  const navigate = useNavigate()
  const isMobile = window.innerWidth < 640
  const { t } = useTranslation()

  const handleNavigate = (path: string, openInNewTab: boolean) => {
    if (openInNewTab) {
      window.open(path, '_blank')
    } else {
      navigate({ to: path || '/' })
    }
  }

  return (
    <footer id='footer-contact' className='w-full bg-primary pb-4 shadow-lg'>
      <ContactUsSection />

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className='mx-auto mt-4 flex w-full max-w-6xl flex-col items-center justify-between gap-4 px-2 pb-6 sm:flex-row'
      >
        <div className='flex flex-1 flex-col items-center text-center sm:items-center sm:text-left'>
          <h2 className='mb-2 text-xl font-bold text-primary-foreground sm:text-2xl'>
            {t(tokens.landing.contact_us_cta)}
          </h2>
          <p className='max-w-full text-sm font-normal text-primary-foreground sm:max-w-xl sm:text-base'>
            {t(tokens.landing.contact_us_text)}
          </p>

          {!isMobile && (
            <div className='mt-2'>
              <div className='flex items-center'>
                <Mail className='mr-2 h-4 w-4 text-muted-foreground' />
                <span className='text-primary-foreground'>
                  contact@mrturing.ai
                </span>
              </div>
            </div>
          )}
        </div>

        <div className='flex w-full flex-1 flex-col items-center justify-center text-center'>
          <h2 className='mb-2 mt-5 text-xl font-bold text-primary-foreground sm:text-2xl'>
            {t(tokens.landing.find_us)}
          </h2>

          <div className='space-y-2'>
            <div className='flex items-center'>
              <a
                href='https://www.google.com/maps/search/?api=1&query=Rua+Alice+Alem+Saad,+855,+Ribeirão+Preto,+SP,+1409-570,+BR'
                target='_blank'
                rel='noopener noreferrer'
                className='mr-2 text-accent hover:text-accent/80'
              >
                <ExternalLink className='h-4 w-4' />
              </a>
              <span className='text-sm text-primary-foreground'>
                Rua Alice Alem Saad, 855, Ribeirão Preto SP, Brazil.
              </span>
            </div>

            <div className='flex items-center'>
              <a
                href='https://www.google.com/maps/search/?api=1&query=2+Simcoe+St.+S+Oshawa,+Ontario+L1H+8C1+CA'
                target='_blank'
                rel='noopener noreferrer'
                className='mr-2 text-accent hover:text-accent/80'
              >
                <ExternalLink className='h-4 w-4' />
              </a>
              <span className='text-sm text-primary-foreground'>
                2 Simcoe St. S Oshawa, Ontario L1H 8C1, Canada.
              </span>
            </div>
          </div>

          <div className='mt-4 flex space-x-2'>
            <span className='w-10'>
              <img
                src='/assets/flags/icon/br.svg'
                className='w-full object-cover'
                alt='Brazil flag'
              />
            </span>
            <span className='w-10'>
              <img
                src='/assets/flags/icon/ca.svg'
                className='w-full object-cover'
                alt='Canada flag'
              />
            </span>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className='mb-2 flex justify-center space-x-2 py-2 sm:mb-0'
      >
        {[
          {
            href: 'https://www.linkedin.com/company/mrturing/',
            icon: <Linkedin className='h-5 w-5' />,
          },
          {
            href: 'https://www.instagram.com/mrturing.ai/',
            icon: <Instagram className='h-5 w-5' />,
          },
          {
            href: 'https://www.facebook.com/mrturing/',
            icon: <Facebook className='h-5 w-5' />,
          },
          {
            href: 'https://www.youtube.com/@mr.turing_ai/',
            icon: <Youtube className='h-5 w-5' />,
          },
        ].map(({ href, icon }, i) => (
          <motion.a
            key={i}
            href={href}
            target='_blank'
            rel='noopener noreferrer'
            whileHover={{ scale: 1.2 }}
            className='rounded-full p-2 text-primary-foreground transition-colors hover:bg-primary/20'
          >
            {icon}
          </motion.a>
        ))}
      </motion.div>

      <div className='mx-auto w-full max-w-6xl px-4 pt-4'>
        <div className='flex flex-col items-center justify-between gap-2 text-center text-sm text-primary-foreground sm:flex-row sm:text-left'>
          <p className='order-2 sm:order-1'>
            &copy; {new Date().getFullYear()} Mr. Turing AI -{' '}
            {t(tokens.landing.copyright)}
          </p>
          <div className='order-1 flex gap-4 sm:order-2'>
            <button
              className='transition-colors hover:text-accent hover:underline'
              onClick={() => handleNavigate(legalLinks.terms, true)}
            >
              {t(tokens.landing.terms)}
            </button>
            <button
              className='transition-colors hover:text-accent hover:underline'
              onClick={() => handleNavigate(legalLinks.privacy, true)}
            >
              {t(tokens.landing.privacy)}
            </button>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
